import { useTypographyStore } from "@redux/slices/typographySlice";
import { GlobalEnum } from "@utils/constants";
import { formatItemValue } from "./types";
import React, { useMemo, useState } from "react";
import { getOptimizedListImageUrl } from "../Utils";
import { View } from "react-native";
import { Image as ExpoImage } from "expo-image";
import {
  getImageSource,
  getIOSImageProps,
  ImageSizePreset,
} from "@utils/image/imageOptimizer";
import TooltipText from "./TooltipText";
import styled from "styled-components/native";
import { css } from "styled-components/native";
import { SpaceType } from "@components/atoms/Spacer/types";
import EmptyImagePreview from "../GalleryView/utils/EmptyImage/EmptyImagePreview";
import { getShapeValue } from "../GalleryView/utils/EmptyImage/utils";
import { useSelector } from "react-redux";
import useScreenOptionsHooks from "../../../hooks/useScreenOptionsHooks";
import { useTranslation } from "react-i18next";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import {
  isLinkedPair,
  isPair,
  isParcel,
} from "@utils/product/productValidators";
import { FALLBACK_IMAGE_ASSET } from "@utils/product/searchResultConstants";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { DefaultTheme } from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import StrikePrice from "../StrikePrice";
import { useHorizontalScrollMultiplier } from "@scenes/searchResults/components/HorizontalScrollMultiplierContext";
import { useConsolidatedStoneTypes } from "@redux/slices/globalSlice";
import { consolidatedStoneTypesIcons } from "../SearchResultGridItem/utils";

const TabletListCellItem = ({
  item,
  isCustomApp,
  mItem,
  userCurrency,
  userInfo,
  _index,
  displayImage,
  fromBulkList,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  handleLinkClick = false,
}: {
  item?: any;
  isCustomApp: boolean;
  mItem?: any;
  userCurrency?: any;
  userInfo?: any;
  _index?: any;
  displayImage?: boolean;
  fromBulkList?: boolean;
  sectionName?: string;
  handleLinkClick?: any;
}) => {
  const { theme } = useScreenOptionsHooks();
  const { t } = useTranslation();
  const [imageUri, setImageUri] = useState(getOptimizedListImageUrl(mItem));
  const ImageComponent = ExpoImage;
  const columnWidth = useSelector((state) => state?.userInfo?.columnWidths);
  const horizontalMultiplier = useHorizontalScrollMultiplier();
  const defaultWidth = Number(item?.deafult_width?.split("px")[0]);
  const scaledDefaultWidth = defaultWidth * horizontalMultiplier;
  const productType: string = mItem?.type;
  const priceMode: number = userInfo.user.price_mode ?? "";
  const storageKey = `${productType}_${sectionName}`;
  const typography = useTypographyStore();
  const consolidatedStoneTypes = useConsolidatedStoneTypes();

  const stoneTypeLowercase = useMemo(
    () =>
      isPair(mItem)
        ? SearchFilterTypeEnumLowercase.PAIRS
        : isParcel(mItem)
        ? SearchFilterTypeEnumLowercase.PARCEL
        : SearchFilterTypeEnumLowercase.SINGLE_STONES,
    [mItem]
  );

  const hasSpecialprice = isCustomApp && mItem?.special_price !== null;
  const isSpecialPriceCell = item?.name === "SpecialPrice";
  const isRetailPriceCell = item?.name === "RetailPrice";

  const getAvailabilityStatus = () => {
    switch (mItem?.available) {
      case GlobalEnum.OUT_ON_MEMO:
        return "error-01";
      case GlobalEnum.ON_HAND:
        return "success-01";
      case GlobalEnum.PLEASE_EMAIL:
      default:
        return "warn-01";
    }
  };

  if (!(item?.is_visible === undefined ? true : item.is_visible)) return null;
  const propertyText =
    sectionName === SearchFilterTypeEnum.PAIRS &&
    isLinkedPair(mItem) &&
    !item?.additional_attributes?.from_master
      ? mItem?.children?.map((child: any) =>
          formatItemValue(
            isCustomApp,
            item.response_key,
            child,
            userCurrency,
            userInfo,
            t,
            true
          )
        )
      : formatItemValue(
          isCustomApp,
          item.response_key,
          mItem,
          userCurrency,
          userInfo,
          t,
          true
        );

  const propertyTooltipText =
    sectionName === SearchFilterTypeEnum.PAIRS &&
    isLinkedPair(mItem) &&
    !item?.additional_attributes?.from_master
      ? mItem?.children?.map((child: any) =>
          formatItemValue(
            isCustomApp,
            item.response_key,
            child,
            userCurrency,
            userInfo,
            t,
            false
          )
        )
      : formatItemValue(
          isCustomApp,
          item.response_key,
          mItem,
          userCurrency,
          userInfo,
          t,
          false
        );

  return (
    <React.Fragment key={_index}>
      {hasSpecialprice ? (
        isSpecialPriceCell ? (
          <InfoContainer
            containerWidth={
              (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                defaultWidth) * horizontalMultiplier
            }
            displayImage={displayImage}
            fromBulkList={fromBulkList}
            maxWidth={scaledDefaultWidth}
            width={
              (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                defaultWidth) * horizontalMultiplier
            }
            alignment={item?.alignment || "flex-start"}
          >
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <VDBIcon
                name={VDBIcons.PERCENTAGE_CIRCLE}
                color={theme["main-04"]}
                size={16}
                variant={IconsVariant.VDBCustom}
              />
              <Spacer size={SpaceType.ExtraSmall} />
              <VDBText
                color={theme["core-05"]}
                type={typography["Minor-M"]}
                numberOfLines={1}
              >
                {propertyText}
              </VDBText>
            </View>
          </InfoContainer>
        ) : isRetailPriceCell ? (
          <InfoContainer
            containerWidth={
              (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                defaultWidth) * horizontalMultiplier
            }
            displayImage={displayImage}
            fromBulkList={fromBulkList}
            maxWidth={scaledDefaultWidth}
            width={
              (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                defaultWidth) * horizontalMultiplier
            }
            alignment={item?.alignment || "flex-start"}
          >
            <StrikePrice
              value={propertyText}
              textColor={theme["error-01"]}
              lineColor={theme["error-01"]}
              typography={typography["Minor-R"]}
              numberOfLines={1}
            />
          </InfoContainer>
        ) : (
          createView(
            item,
            displayImage,
            theme,
            getAvailabilityStatus,
            imageUri,
            ImageComponent,
            setImageUri,
            mItem,
            columnWidth,
            storageKey,
            priceMode,
            scaledDefaultWidth,
            fromBulkList,
            propertyText,
            typography,
            propertyTooltipText,
            handleLinkClick,
            horizontalMultiplier,
            consolidatedStoneTypes,
            stoneTypeLowercase
          )
        )
      ) : (
        createView(
          item,
          displayImage,
          theme,
          getAvailabilityStatus,
          imageUri,
          ImageComponent,
          setImageUri,
          mItem,
          columnWidth,
          storageKey,
          priceMode,
          scaledDefaultWidth,
          fromBulkList,
          propertyText,
          typography,
          propertyTooltipText,
          handleLinkClick,
          horizontalMultiplier,
          consolidatedStoneTypes,
          stoneTypeLowercase
        )
      )}
    </React.Fragment>
  );
};

const PressableColumn = styled.Pressable`
  justify-content: center;
  align-items: center;
`;

const ImageContainer = styled.View`
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background-color: ${({ theme }) => theme["core-03"]};
`;
const ImageOverlay = styled.View<{
  containerWidth: string;
  displayImage: boolean;
}>`
  flex-direction: row;
  position: relative;
  padding-top: 8px;
  width: ${({ containerWidth }) => containerWidth};
  ${({ displayImage }) =>
    displayImage &&
    css`
      justify-content: center;
      overflow-x: scroll;
    `}
`;

const DotOverlay = styled.View`
  flex-direction: row;
  position: relative;
  justify-content: center;
  align-items: center;
`;

const AvailabilityDot = styled.View<any>`
  height: 8px;
  width: 8px;
  border-radius: 7px;
  border: 1px;
  border-color: ${({ theme }) => theme["cont-00"]};
  background-color: ${({ bgColor }) => bgColor};
  position: absolute;
  top: 51px;
  right: 54px;
  z-index: 2;
`;

const InfoContainer = styled.View<{
  containerWidth?: string;
  displayImage?: boolean;
  alignment?: string;
  fromBulkList?: boolean;
  maxWidth?: string;
  width?: string;
}>`
  padding-top: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-bottom: ${({ displayImage }) =>
    displayImage ? `${SpaceType.Big}px` : "5px"};
  padding-left: ${SpaceType.Basic}px;
  padding-right: ${SpaceType.Small}px;
  margin-right: 3px;
  display: flex;
  width: ${({ width }) => (width ? `${width}px` : "100%")};
  min-width: ${({ containerWidth }) => containerWidth}px;
  flex-direction: ${({ direction }) => direction ?? "column"};
  align-items: ${({ alignment }) => alignment};
  ${({ displayImage }) =>
    !displayImage &&
    css`
      justify-content: center;
    `};
  ${({ theme }) =>
    theme.isTablet &&
    css`
      padding-right: 8px;
      padding-left: 8px;
    `};
`;

const PairChildItemsContainer = styled.View`
  flex-direction: column;
  gap: 6px;
`;

export default TabletListCellItem;

function createView(
  item: any,
  displayImage: boolean | undefined,
  theme: DefaultTheme,
  getAvailabilityStatus: () => "error-01" | "success-01" | "warn-01",
  imageUri: string,
  ImageComponent: typeof ExpoImage,
  setImageUri: React.Dispatch<React.SetStateAction<string>>,
  mItem: any,
  columnWidth: any,
  storageKey: string,
  priceMode: number,
  defaultWidth: number,
  fromBulkList: boolean | undefined,
  propertyText: any,
  typography: any,
  propertyTooltipText: any,
  handleLinkClick: any,
  horizontalMultiplier: number,
  consolidatedStoneTypes: any,
  stoneTypeLowercase: string
): React.ReactNode {
  const consolidatedStoneTypesIconsData = consolidatedStoneTypesIcons(
    consolidatedStoneTypes
  );
  return item.response_key === "available" ? (
    displayImage ? (
      <ImageOverlay
        containerWidth={item?.deafult_width}
        displayImage={displayImage}
      >
        <AvailabilityDot bgColor={theme[getAvailabilityStatus()]} />
        <ImageContainer>
          {imageUri !== "" ? (
            <ImageComponent
              source={getImageSource(imageUri, ImageSizePreset.LIST_THUMB)}
              contentFit="cover"
              {...getIOSImageProps()}
              onError={() => setImageUri(FALLBACK_IMAGE_ASSET)}
              style={{
                width: 48,
                height: 48,
                borderRadius: 8,
                position: "relative",
                zIndex: 1,
              }}
            />
          ) : (
            <View style={{ width: 48, height: 48 }}>
              <EmptyImagePreview
                shape={getShapeValue(mItem)}
                size={40}
                isPair={isPair(mItem) ?? false}
              />
            </View>
          )}
        </ImageContainer>
      </ImageOverlay>
    ) : (
      <InfoContainer
        containerWidth={
          (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
            defaultWidth) * horizontalMultiplier
        }
        displayImage={displayImage}
        fromBulkList={fromBulkList}
        maxWidth={defaultWidth}
        width={
          (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
            defaultWidth) * horizontalMultiplier
        }
      >
        <DotOverlay>
          <TooltipText
            text={propertyText}
            tooltip={propertyText}
            color={theme[getAvailabilityStatus()]}
            textAlignment={item?.alignment === "flex-end" ? "right" : "left"}
            type={typography["Minor-R"]}
            enableTooltip={item?.name === "available"}
            isAvailableStatusView={true}
          />
        </DotOverlay>
      </InfoContainer>
    )
  ) : item.response_key === "pair" && isPair(mItem) ? (
    <InfoContainer
      containerWidth={
        (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
          defaultWidth) * horizontalMultiplier
      }
      displayImage={displayImage}
      fromBulkList={fromBulkList}
      maxWidth={defaultWidth}
      width={
        (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
          defaultWidth) * horizontalMultiplier
      }
      alignment={item?.alignment || "flex-start"}
    >
      <VDBIcon
        name={VDBIcons.FIND_A_MATCH}
        color={theme["core-05"]}
        size={20}
        variant={IconsVariant.VDBCustom}
      />
    </InfoContainer>
  ) : item.response_key === "stone_type_icon" ? (
    <InfoContainer
      containerWidth={
        columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
      }
      displayImage={displayImage}
      fromBulkList={fromBulkList}
      maxWidth={defaultWidth}
      width={
        columnWidth?.[storageKey]?.[priceMode]?.[item.name] || defaultWidth
      }
      alignment={item?.alignment || "flex-start"}
    >
      <VDBIcon
        name={consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.data}
        variant={consolidatedStoneTypesIconsData?.[stoneTypeLowercase]?.type}
        color={theme["core-05"]}
        size={20}
      />
    </InfoContainer>
  ) : (
    <InfoContainer
      containerWidth={
        (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
          defaultWidth) * horizontalMultiplier
      }
      displayImage={displayImage}
      alignment={item?.alignment || "flex-start"}
      fromBulkList={fromBulkList}
      maxWidth={defaultWidth}
      width={
        (Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
          defaultWidth) * horizontalMultiplier
      }
    >
      {Array.isArray(propertyText) && propertyText.length ? (
        <PairChildItemsContainer>
          {propertyText.map((textItem: any, textIndex: any) => {
            // if (textIndex > 0 && propertyText[0] === textItem) return null;
            const enableToolTip =
              item?.enable_tooltip &&
              mItem?.children?.[textIndex]?.[item?.popover_data_response_key];
            const showAsPopOver =
              item?.show_popover_data &&
              mItem?.children?.[textIndex]?.[item?.popover_data_response_key];
            const commonProps = {
              text: textItem,
              tooltip: item?.tooltip_text
                ? item?.tooltip_text
                : propertyTooltipText[textIndex],
              type: typography["Minor-R"],
              textAlignment:
                item?.alignment === "flex-end"
                  ? "right"
                  : item?.alignment === "center"
                  ? "center"
                  : "left",
              defaultWidth:
                ((Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                  defaultWidth) -
                  5) *
                  horizontalMultiplier +
                "px",
              enableTooltip:
                theme.isLargeScreen &&
                (item?.name === "Color" ||
                  item?.name === "vendor_name" ||
                  item?.name === "location" ||
                  enableToolTip),
            };
            return showAsPopOver ? (
              <PressableColumn
                onPress={(e) => {
                  e.preventDefault();
                  handleLinkClick(
                    mItem?.children?.[textIndex],
                    item?.popover_data_response_key
                  );
                }}
              >
                <TooltipText
                  {...commonProps}
                  color={theme["link-static"]}
                  hoverColor={theme["link-hover"]}
                />
              </PressableColumn>
            ) : (
              <TooltipText
                {...commonProps}
                color={theme["core-05"]}
                hoverColor={theme["core-05"]}
              />
            );
          })}
        </PairChildItemsContainer>
      ) : (
        (() => {
          const enableToolTip =
            item?.enable_tooltip && mItem?.[item?.popover_data_response_key];
          const showAsPopOver =
            item?.show_popover_data && mItem?.[item?.popover_data_response_key];
          const commonProps = {
            text: propertyText,
            tooltip: item?.tooltip_text
              ? item?.tooltip_text
              : propertyTooltipText,
            type: typography["Minor-R"],
            textAlignment:
              item?.alignment === "flex-end"
                ? "right"
                : item?.alignment === "center"
                ? "center"
                : "left",
            defaultWidth:
              ((Number(columnWidth?.[storageKey]?.[priceMode]?.[item.name]) ||
                defaultWidth) -
                5) *
                horizontalMultiplier +
              "px",
            enableTooltip:
              theme.isLargeScreen &&
              (item?.name === "Color" ||
                item?.name === "vendor_name" ||
                item?.name === "location" ||
                enableToolTip),
          };
          return showAsPopOver ? (
            <PressableColumn
              onPress={(e) => {
                e.preventDefault();
                handleLinkClick(mItem, item?.popover_data_response_key);
              }}
            >
              <TooltipText
                {...commonProps}
                color={theme["link-static"]}
                hoverColor={theme["link-hover"]}
              />
            </PressableColumn>
          ) : (
            <TooltipText
              {...commonProps}
              color={theme["core-05"]}
              hoverColor={theme["core-05"]}
            />
          );
        })()
      )}
    </InfoContainer>
  );
}
